import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { String } from '@blade/std-lib'
import {
  useWalletAssetBalance,
  useWalletAssetBalanceUsd,
} from '@root/entities/wallet/hooks'
import { CurrencySelectorDialog } from '@root/widgets/currency-selector/ui/desktop'

import { AssetNodeDefs } from '../../model/nodes'

import { NodeBalanceOutlet } from './node-amount-outlet'
import { NodeRefreshButton } from './node-refresh-button'
import { PlaceholderNode } from './placeholder-node'

import type { DistributeFundsModel } from '../../atoms'
import type { CurrencyModel } from '@root/entities/currency'
import type { WalletModel } from '@root/entities/wallet'
import type { StyleProps } from '@root/shared/react'

type Props = {
  model: DistributeFundsModel
  ref?: React.Ref<HTMLDivElement>
} & StyleProps

export const AssetNode: React.FC<Props> = ({ model, style, ref }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [currency, setCurrency] = useAtom(model.currency)
  const [wallet] = useAtom(model.sourceWallet)

  const node =
    currency && wallet ? (
      <AssetNodeReady
        wallet={wallet}
        currency={currency}
        style={style}
        ref={ref}
        onRefresh={() => {
          setOpen(true)
        }}
      />
    ) : (
      <PlaceholderNode
        style={style}
        ref={ref}
        label={t('asset')}
        onClick={() => {
          setOpen(true)
        }}
      />
    )

  return (
    <>
      {node}
      {wallet ? (
        <CurrencySelectorDialog
          title={t('wallet.distribute.select-asset')}
          sets={['wallet']}
          wallet={wallet}
          open={open}
          onOpenChange={setOpen}
          onCurrencySelect={(currency) => {
            setCurrency(currency)
            setOpen(false)
          }}
        />
      ) : undefined}
    </>
  )
}

const AssetNodeReady = ({
  wallet,
  currency,
  style,
  onRefresh,
  ref,
}: {
  currency: CurrencyModel
  wallet: WalletModel
  onRefresh: () => void
  ref?: React.Ref<HTMLDivElement>
} & StyleProps) => {
  const { t } = useTranslation()

  const balanceCurrency = useWalletAssetBalance({ currency, wallet })
  const balanceUsd = useWalletAssetBalanceUsd({ currency, wallet })

  return (
    <Root style={style} ref={ref}>
      <Header>
        <Label>{String.capitalize(t('asset'))}</Label>
        <NodeRefreshButton onClick={onRefresh} />
      </Header>
      <NodeBalanceOutlet
        balanceCurrency={balanceCurrency}
        balanceUsd={balanceUsd}
        currency={currency}
      />
    </Root>
  )
}

const Root = styled.div`
  border-radius: 8px;
  padding-inline: 3px;
  padding-block: 3px;

  background: var(--background-2);
  border: 1px solid var(--background-3);

  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  align-content: center;

  height: ${AssetNodeDefs.height}px;
  width: ${AssetNodeDefs.width}px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-inline: 5px;
`

const Label = styled.div`
  color: var(--text-11);

  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`
